import { useState, useCallback, useEffect, useRef, Suspense } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { supabase } from '@/lib/supabase';
import { gcsGetPublicUrl, detectUrlType } from '@/lib/gcsStorage';
import { useGcsUpload } from '@/hooks/useGcsUpload';
import { useAuth } from '@/contexts/AuthContext';
import { useComplianceDocs } from '@/hooks/useComplianceDocs';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import {
  Folder,
  FileText,
  ChevronRight,
  ChevronLeft,
  Download,
  Upload,
  AlertCircle,
  Clock,
  CheckCircle2,
  Circle,
  MoreVertical,
  History,
  Trash2,
  ExternalLink,
  FileUp,
  Undo2,
  User,
  CalendarDays
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

// Lazy load the upload modals for better performance
const UploadModal = lazyWithRetry(() => import('@/components/modals/UploadModal'));
const SiteDocumentUploadModal = lazyWithRetry(() => import('@/components/modals/SiteDocumentUploadModal'));

// Page size options
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

// Status filter options by folder type
export const getStatusOptions = (folderType) => {
  if (folderType === 'personnel') {
    return [
      { value: 'all', label: 'All Statuses' },
      { value: 'expired', label: 'Expired' },
      { value: 'expiring', label: 'Expiring Soon' },
      { value: 'valid', label: 'Valid' },
      { value: 'missing', label: 'Outstanding' }
    ];
  }
  if (folderType === 'site') {
    return [
      { value: 'all', label: 'All Statuses' },
      { value: 'overdue', label: 'Overdue' },
      { value: 'due_soon', label: 'Due Soon' },
      { value: 'compliant', label: 'Compliant' },
      { value: 'not_started', label: 'Outstanding' }
    ];
  }
  return [];
};

/**
 * Filter content component for the ToolHeader popover
 */
export function ComplianceFilterContent({
  currentFolder,
  statusFilter,
  onStatusFilterChange,
  pageSize,
  onPageSizeChange,
  stats
}) {
  if (!currentFolder) return null;

  const statusOptions = getStatusOptions(currentFolder);

  return (
    <div className="space-y-4">
      {/* Status Filter */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-gray-700">Status</Label>
        <Select
          value={statusFilter || 'all'}
          onValueChange={(value) => onStatusFilterChange(value === 'all' ? null : value)}
        >
          <SelectTrigger className="w-full h-9 text-sm">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map(option => (
              <SelectItem key={option.value} value={option.value} className="text-sm">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Page Size */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-gray-700">Items per page</Label>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(parseInt(value))}
        >
          <SelectTrigger className="w-full h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map(size => (
              <SelectItem key={size} value={size.toString()} className="text-sm">
                {size} items
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="pt-2 border-t">
          <Label className="text-xs font-medium text-gray-700 mb-2 block">Summary</Label>
          <div className="space-y-1 text-xs">
            {currentFolder === 'personnel' && (
              <>
                {stats.personnel?.expired > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expired</span>
                    <span className="text-red-600 font-medium">{stats.personnel.expired}</span>
                  </div>
                )}
                {stats.personnel?.expiring > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expiring Soon</span>
                    <span className="text-amber-600 font-medium">{stats.personnel.expiring}</span>
                  </div>
                )}
                {stats.personnel?.valid > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Valid</span>
                    <span className="text-green-600 font-medium">{stats.personnel.valid}</span>
                  </div>
                )}
                {stats.personnel?.missing > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Outstanding</span>
                    <span className="text-gray-500 font-medium">{stats.personnel.missing}</span>
                  </div>
                )}
              </>
            )}
            {currentFolder === 'site' && (
              <>
                {stats.site?.overdue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Overdue</span>
                    <span className="text-red-600 font-medium">{stats.site.overdue}</span>
                  </div>
                )}
                {stats.site?.dueSoon > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Due Soon</span>
                    <span className="text-amber-600 font-medium">{stats.site.dueSoon}</span>
                  </div>
                )}
                {stats.site?.compliant > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Compliant</span>
                    <span className="text-green-600 font-medium">{stats.site.compliant}</span>
                  </div>
                )}
                {stats.site?.notStarted > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Outstanding</span>
                    <span className="text-gray-500 font-medium">{stats.site.notStarted}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Status configuration
const statusConfig = {
  // Personnel statuses
  expired: { label: 'Expired', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100' },
  expiring: { label: 'Expiring Soon', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100' },
  valid: { label: 'Valid', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100' },
  missing: { label: 'Outstanding', icon: Circle, color: 'text-gray-400', bg: 'bg-gray-100' },
  // Site statuses
  overdue: { label: 'Overdue', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100' },
  due_soon: { label: 'Due Soon', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100' },
  compliant: { label: 'Compliant', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100' },
  not_started: { label: 'Outstanding', icon: Circle, color: 'text-gray-400', bg: 'bg-gray-100' }
};

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.missing;
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </div>
  );
}

function ParentFolderRow({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 border-b border-gray-100 transition-colors text-left group"
    >
      <Folder className="h-5 w-5 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm text-gray-600">..</span>
      </div>
      <ChevronLeft className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
    </button>
  );
}

function FolderRow({ name, description, count, urgentCount, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 border-b border-gray-100 transition-colors text-left group"
    >
      <Folder className="h-5 w-5 text-amber-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-gray-900">{name}</span>
          {urgentCount > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">
              {urgentCount} need attention
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <span className="text-xs text-gray-400">{count} items</span>
      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
    </button>
  );
}

function FileRow({ item, type, onDownload, onUpload, onRemove, onViewHistory, onOpenFile, onEditResponsible, onEditDueDate, onEditExpiry }) {
  const isPersonnel = type === 'personnel';
  const isSite = type === 'site';
  const hasFile = isPersonnel ? !!item.evidenceUrl : (!!item.evidenceUrl || !!item.evidenceFileId);

  const name = isPersonnel ? item.requirementName : item.name;
  const person = isPersonnel ? item.userName : item.responsiblePersonName;
  const avatar = isPersonnel ? item.userAvatar : item.responsiblePersonAvatar;
  const dateValue = isPersonnel ? item.expiryDate : item.nextDueDate;
  const status = item.complianceStatus;
  const hasVersionHistory = item.evidenceVersionHistory && item.evidenceVersionHistory.length > 0;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    // Compare dates only (not times) - a certificate is valid until end of day
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDay = new Date(d);
    expiryDay.setHours(0, 0, 0, 0);

    if (expiryDay < today) {
      return `Expired ${formatDistanceToNow(d, { addSuffix: false })} ago`;
    }
    return format(d, 'dd MMM yyyy');
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 transition-colors group">
      {/* Icon */}
      <FileText className={`h-4 w-4 flex-shrink-0 ${hasFile ? 'text-blue-500' : 'text-gray-300'}`} />

      {/* Name */}
      <div className="flex-1 min-w-0 w-[200px]">
        <p className="text-sm text-gray-900 truncate">{name}</p>
        {item.category && (
          <p className="text-[10px] text-gray-400 truncate">{item.category}</p>
        )}
      </div>

      {/* Person */}
      <div className="flex items-center gap-2 w-[180px] flex-shrink-0">
        {person ? (
          <>
            <Avatar className="h-6 w-6">
              <AvatarImage src={avatar} />
              <AvatarFallback className="text-[10px]">{getInitials(person)}</AvatarFallback>
            </Avatar>
            <span className="text-xs text-gray-600 truncate">{person}</span>
          </>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </div>

      {/* Status */}
      <div className="w-[120px] flex-shrink-0">
        <StatusBadge status={status} />
      </div>

      {/* Date */}
      <div className="w-[120px] flex-shrink-0">
        <span className={`text-xs ${status === 'expired' || status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
          {formatDate(dateValue)}
        </span>
      </div>

      {/* Actions */}
      <div className="w-[100px] flex items-center justify-end gap-1 flex-shrink-0">
        {hasFile ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onDownload(item)}
              title="Download"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            {/* 3-dot menu for documents with files */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onOpenFile(item.evidenceUrl)}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in new tab
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onUpload(item, type)}>
                  <FileUp className="h-4 w-4 mr-2" />
                  Upload new version
                </DropdownMenuItem>
                {hasVersionHistory && (
                  <DropdownMenuItem onClick={() => onViewHistory(item, type)}>
                    <History className="h-4 w-4 mr-2" />
                    View history ({item.evidenceVersionHistory.length})
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {isPersonnel && (
                  <DropdownMenuItem onClick={() => onEditExpiry(item)}>
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Edit expiry date
                  </DropdownMenuItem>
                )}
                {isSite && (
                  <>
                    <DropdownMenuItem onClick={() => onEditResponsible(item)}>
                      <User className="h-4 w-4 mr-2" />
                      Assign responsible
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onEditDueDate(item)}>
                      <CalendarDays className="h-4 w-4 mr-2" />
                      Edit due date
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onRemove(item, type)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove {isPersonnel ? 'certificate' : 'document'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : hasVersionHistory ? (
          // No current file but has version history - show 3-dot menu with upload and history options
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onUpload(item, type)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onViewHistory(item, type)}>
                <History className="h-4 w-4 mr-2" />
                View history ({item.evidenceVersionHistory.length})
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isPersonnel && (
                <DropdownMenuItem onClick={() => onEditExpiry(item)}>
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Edit expiry date
                </DropdownMenuItem>
              )}
              {isSite && (
                <>
                  <DropdownMenuItem onClick={() => onEditResponsible(item)}>
                    <User className="h-4 w-4 mr-2" />
                    Assign responsible
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onEditDueDate(item)}>
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Edit due date
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : isSite ? (
          // Site document with no file and no history - show upload button and 3-dot for edit options
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => onUpload(item, type)}
            >
              <Upload className="h-3 w-3" />
              Upload
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onEditResponsible(item)}>
                  <User className="h-4 w-4 mr-2" />
                  Assign responsible
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEditDueDate(item)}>
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Edit due date
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          // Personnel certificate with no file and no history - just upload button
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => onUpload(item, type)}
          >
            <Upload className="h-3 w-3" />
            Upload
          </Button>
        )}
      </div>
    </div>
  );
}

export function ComplianceExplorer({
  projectId,
  projectIds,
  isStackView,
  isGlobalView = false,
  selectedSpaceFilter,
  // State controlled by parent
  currentFolder = null,
  onFolderChange,
  searchQuery = '',
  statusFilter = null,
  pageSize = 25,
  // Callback to provide stats to parent
  onStatsChange
}) {
  const { userProfile } = useAuth();
  const { getViewableUrl } = useGcsUpload();

  // Internal pagination state
  const [personnelPage, setPersonnelPage] = useState(1);
  const [sitePage, setSitePage] = useState(1);

  // Upload modal state (for certificate uploads to specific requirements)
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedUploadItem, setSelectedUploadItem] = useState(null);

  // Site document upload modal state
  const [siteUploadModalOpen, setSiteUploadModalOpen] = useState(false);
  const [selectedSiteUploadItem, setSelectedSiteUploadItem] = useState(null);

  // Version history dialog state
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionHistoryItem, setVersionHistoryItem] = useState(null);
  const [versionHistoryType, setVersionHistoryType] = useState(null); // 'personnel' or 'site'

  // Edit expiry date dialog state (for personnel certificates)
  const [editExpiryOpen, setEditExpiryOpen] = useState(false);
  const [editExpiryItem, setEditExpiryItem] = useState(null);
  const [selectedExpiryDate, setSelectedExpiryDate] = useState(null);

  // Edit responsible person dialog state
  const [editResponsibleOpen, setEditResponsibleOpen] = useState(false);
  const [editResponsibleItem, setEditResponsibleItem] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

  // Edit due date dialog state
  const [editDueDateOpen, setEditDueDateOpen] = useState(false);
  const [editDueDateItem, setEditDueDateItem] = useState(null);
  const [selectedDueDate, setSelectedDueDate] = useState(null);

  // Drag-and-drop state for file upload
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragTimeoutRef = useRef(null);

  // Reset pagination when filters change
  useEffect(() => {
    setPersonnelPage(1);
    setSitePage(1);
  }, [searchQuery, statusFilter, pageSize, currentFolder]);

  // Calculate offsets for pagination
  const personnelOffset = (personnelPage - 1) * pageSize;
  const siteOffset = (sitePage - 1) * pageSize;

  const {
    personnelCerts,
    siteDocs,
    stats,
    loading,
    refetch
  } = useComplianceDocs(projectId, {
    projectIds,
    isStackView,
    isGlobalView,
    selectedSpaceFilter,
    enabled: !!(projectId || isStackView || isGlobalView),
    personnelLimit: pageSize,
    personnelOffset,
    siteLimit: pageSize,
    siteOffset,
    statusFilter,
    searchQuery: searchQuery || null
  });

  // Calculate urgent counts
  const personnelUrgent = (stats?.personnel?.expired || 0) + (stats?.personnel?.expiring || 0) + (stats?.personnel?.missing || 0);
  const siteUrgent = (stats?.site?.overdue || 0) + (stats?.site?.dueSoon || 0) + (stats?.site?.notStarted || 0);

  // Get filtered totals for pagination
  const personnelFilteredTotal = stats?.personnel?.filteredTotal || stats?.personnel?.total || 0;
  const siteFilteredTotal = stats?.site?.filteredTotal || stats?.site?.total || 0;

  // Notify parent of stats changes
  useEffect(() => {
    if (onStatsChange && stats) {
      onStatsChange(stats);
    }
  }, [stats, onStatsChange]);

  // Calculate total pages
  const personnelTotalPages = Math.ceil(personnelFilteredTotal / pageSize) || 1;
  const siteTotalPages = Math.ceil(siteFilteredTotal / pageSize) || 1;

  // Get current page and total for active folder
  const currentPage = currentFolder === 'personnel' ? personnelPage : sitePage;
  const totalPages = currentFolder === 'personnel' ? personnelTotalPages : siteTotalPages;
  const setCurrentPage = currentFolder === 'personnel' ? setPersonnelPage : setSitePage;
  const filteredTotal = currentFolder === 'personnel' ? personnelFilteredTotal : siteFilteredTotal;

  const handleDownload = useCallback(async (item) => {
    let url = item.evidenceUrl;
    if (!url) {
      toast.error('No file available');
      return;
    }

    try {
      // Resolve the URL for viewing (handles GCS, Supabase, and relative paths)
      const resolvedUrl = await getViewableUrl(url, 'certificates');
      if (resolvedUrl) url = resolvedUrl;

      // Fetch the file as a blob to force download
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch file');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Extract filename from URL or use a default
      let urlPath;
      try {
        urlPath = new URL(item.evidenceUrl).pathname;
      } catch {
        // evidenceUrl may be a relative storage path, not a full URL
        urlPath = item.evidenceUrl;
      }
      const fileName = urlPath.split('/').pop() || item.requirementName || 'document';

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  }, []);

  // Handle opening file in new tab - uses Google Docs viewer for documents
  const handleOpenFile = useCallback(async (url) => {
    if (!url) {
      toast.error('No file available');
      return;
    }

    let openUrl = await getViewableUrl(url, 'certificates') || url;

    // Get file extension from URL
    const urlLower = url.toLowerCase();
    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i.test(urlLower);
    const isPdf = /\.pdf(\?|$)/i.test(urlLower);
    const isDocument = /\.(doc|docx|xls|xlsx|ppt|pptx|txt|rtf|csv|odt|ods|odp)(\?|$)/i.test(urlLower);

    if (isImage || isPdf) {
      // Images and PDFs can be opened directly - most browsers display them inline
      window.open(openUrl, '_blank');
    } else if (isDocument) {
      // Use Google Docs viewer for office documents
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(openUrl)}&embedded=false`;
      window.open(viewerUrl, '_blank');
    } else {
      // For other files, try opening directly
      window.open(openUrl, '_blank');
    }
  }, []);

  // Handle upload click - opens appropriate dialog based on item type
  const handleUpload = useCallback((item, type) => {
    if (type === 'personnel') {
      // Build the selectedCell object that UploadModal expects
      setSelectedUploadItem({
        member: {
          id: item.userId,
          name: item.userName,
        },
        requirement: {
          id: item.requirementId,
          name: item.requirementName,
          project_id: item.projectId,
        },
        // Store original item for reference
        originalItem: item
      });
      setUploadModalOpen(true);
    } else {
      // Site documents - open site upload modal
      setSelectedSiteUploadItem({
        id: item.id,
        name: item.name,
        category: item.category,
        projectId: item.projectId || projectId,
        // Store original item for reference
        originalItem: item
      });
      setSiteUploadModalOpen(true);
    }
  }, [projectId]);

  // Handle viewing version history
  const handleViewHistory = useCallback((item, type) => {
    setVersionHistoryItem(item);
    setVersionHistoryType(type);
    setVersionHistoryOpen(true);
  }, []);

  // Handle reverting to a previous version
  const handleRevertToVersion = useCallback(async (version, index) => {
    if (!versionHistoryItem?.id || !version?.url) {
      toast.error('Invalid version data');
      return;
    }

    const loadingToast = toast.loading('Restoring version...');

    try {
      // Build new version history - remove the version being restored and add current (if exists) to history
      let newVersionHistory = [...(versionHistoryItem.evidenceVersionHistory || [])];

      // Add current document to history if it exists (before replacing)
      if (versionHistoryItem.evidenceUrl) {
        if (versionHistoryType === 'personnel') {
          newVersionHistory.push({
            url: versionHistoryItem.evidenceUrl,
            expiry_date: versionHistoryItem.expiryDate,
            certificate_number: versionHistoryItem.certificateNumber,
            uploaded_at: versionHistoryItem.completedDate,
            replaced_at: new Date().toISOString()
          });
        } else {
          newVersionHistory.push({
            url: versionHistoryItem.evidenceUrl,
            file_name: versionHistoryItem.evidenceFileName,
            file_id: versionHistoryItem.evidenceFileId,
            uploaded_at: versionHistoryItem.lastCompletedDate,
            replaced_at: new Date().toISOString()
          });
        }
      }

      // Remove the version being restored from history
      newVersionHistory = newVersionHistory.filter((_, i) => i !== index);

      if (versionHistoryType === 'personnel') {
        // Update user_qualifications with restored version
        const { error } = await supabase
          .from('user_qualifications')
          .update({
            evidence_url: version.url,
            expiry_date: version.expiry_date || null,
            certificate_number: version.certificate_number || null,
            evidence_version_history: newVersionHistory,
            status: 'completed',
            completed_date: new Date().toISOString(),
            updated_date: new Date().toISOString()
          })
          .eq('id', versionHistoryItem.id);

        if (error) throw error;
      } else {
        // Update site_compliance_requirements with restored version
        const { error } = await supabase
          .from('site_compliance_requirements')
          .update({
            evidence_url: version.url,
            evidence_file_id: version.file_id || null,
            evidence_file_name: version.file_name || null,
            evidence_version_history: newVersionHistory,
            status: 'compliant',
            last_completed_date: new Date().toISOString()
          })
          .eq('id', versionHistoryItem.id);

        if (error) throw error;
      }

      toast.success('Version restored', {
        id: loadingToast,
        description: `Restored "${version.file_name || 'previous version'}"`
      });

      // Close dialog and refresh data
      setVersionHistoryOpen(false);
      setVersionHistoryItem(null);
      setVersionHistoryType(null);
      refetch();
    } catch (error) {
      console.error('Error restoring version:', error);
      toast.error('Failed to restore version', { id: loadingToast });
    }
  }, [versionHistoryItem, versionHistoryType, refetch]);

  // Handle viewing a version file (opens in new tab with signed URL if needed)
  const handleViewVersionFile = useCallback(async (url) => {
    if (!url) {
      toast.error('No file available');
      return;
    }

    const openUrl = await getViewableUrl(url, 'certificates') || url;
    window.open(openUrl, '_blank');
  }, []);

  // Handle opening edit responsible person dialog
  const handleEditResponsible = useCallback(async (item) => {
    setEditResponsibleItem(item);
    setEditResponsibleOpen(true);

    // Fetch team members for the project
    setLoadingTeamMembers(true);
    try {
      const targetProjectId = item.projectId || projectId;

      // First try team_members for the project
      const { data: teamData, error: teamError } = await supabase
        .from('team_members')
        .select(`
          user_id,
          user_profiles!inner(
            id,
            first_name,
            last_name,
            avatar_url,
            email
          )
        `)
        .eq('project_id', targetProjectId);

      if (teamError) throw teamError;

      if (teamData && teamData.length > 0) {
        const users = teamData.map(member => ({
          id: member.user_profiles.id,
          first_name: member.user_profiles.first_name,
          last_name: member.user_profiles.last_name,
          avatar_url: member.user_profiles.avatar_url,
          email: member.user_profiles.email,
        }));
        setTeamMembers(users);
      } else {
        // Fallback to company members
        const { data: projectData } = await supabase
          .from('projects')
          .select('company_id')
          .eq('id', targetProjectId)
          .single();

        if (projectData?.company_id) {
          const { data: companyData } = await supabase
            .from('company_members')
            .select(`
              user_id,
              user_profiles!inner(
                id,
                first_name,
                last_name,
                avatar_url,
                email
              )
            `)
            .eq('company_id', projectData.company_id);

          if (companyData) {
            const users = companyData.map(member => ({
              id: member.user_profiles.id,
              first_name: member.user_profiles.first_name,
              last_name: member.user_profiles.last_name,
              avatar_url: member.user_profiles.avatar_url,
              email: member.user_profiles.email,
            }));
            setTeamMembers(users);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoadingTeamMembers(false);
    }
  }, [projectId]);

  // Handle saving responsible person
  const handleSaveResponsible = useCallback(async (userId) => {
    if (!editResponsibleItem?.id) return;

    const loadingToast = toast.loading('Updating responsible person...');
    try {
      const { error } = await supabase
        .from('site_compliance_requirements')
        .update({
          responsible_person_id: userId || null,
          updated_date: new Date().toISOString()
        })
        .eq('id', editResponsibleItem.id);

      if (error) throw error;

      toast.success('Responsible person updated', { id: loadingToast });
      setEditResponsibleOpen(false);
      setEditResponsibleItem(null);
      refetch();
    } catch (error) {
      console.error('Error updating responsible person:', error);
      toast.error('Failed to update', { id: loadingToast });
    }
  }, [editResponsibleItem, refetch]);

  // Handle opening edit due date dialog
  const handleEditDueDate = useCallback((item) => {
    setEditDueDateItem(item);
    setSelectedDueDate(item.nextDueDate ? new Date(item.nextDueDate) : null);
    setEditDueDateOpen(true);
  }, []);

  // Handle saving due date
  const handleSaveDueDate = useCallback(async () => {
    if (!editDueDateItem?.id) return;

    const loadingToast = toast.loading('Updating due date...');
    try {
      const { error } = await supabase
        .from('site_compliance_requirements')
        .update({
          next_due_date: selectedDueDate ? selectedDueDate.toISOString() : null,
          updated_date: new Date().toISOString()
        })
        .eq('id', editDueDateItem.id);

      if (error) throw error;

      toast.success('Due date updated', { id: loadingToast });
      setEditDueDateOpen(false);
      setEditDueDateItem(null);
      setSelectedDueDate(null);
      refetch();
    } catch (error) {
      console.error('Error updating due date:', error);
      toast.error('Failed to update', { id: loadingToast });
    }
  }, [editDueDateItem, selectedDueDate, refetch]);

  // Handle removing/unlinking a document from a requirement
  const handleRemoveDocument = useCallback(async (item, type) => {
    if (!item.id) {
      toast.error('Invalid item');
      return;
    }

    const isPersonnel = type === 'personnel';
    const loadingToast = toast.loading(isPersonnel ? 'Removing certificate...' : 'Removing document...');

    try {
      // Build version history - add current document to history before removing
      let versionHistory = item.evidenceVersionHistory || [];
      if (item.evidenceUrl) {
        if (isPersonnel) {
          versionHistory = [
            ...versionHistory,
            {
              url: item.evidenceUrl,
              expiry_date: item.expiryDate,
              certificate_number: item.certificateNumber,
              uploaded_at: item.completedDate,
              removed_at: new Date().toISOString()
            }
          ];
        } else {
          versionHistory = [
            ...versionHistory,
            {
              url: item.evidenceUrl,
              file_name: item.evidenceFileName,
              file_id: item.evidenceFileId,
              uploaded_at: item.lastCompletedDate,
              removed_at: new Date().toISOString()
            }
          ];
        }
      }

      if (isPersonnel) {
        // Update user_qualifications to remove the evidence but keep history
        // Note: status can only be NULL, 'completed', or 'expired' per check constraint
        const { error } = await supabase
          .from('user_qualifications')
          .update({
            evidence_url: null,
            certificate_number: null,
            evidence_version_history: versionHistory,
            status: null,
            completed_date: null,
            updated_date: new Date().toISOString()
          })
          .eq('id', item.id);

        if (error) throw error;

        toast.success('Certificate removed', {
          id: loadingToast,
          description: versionHistory.length > 0
            ? `Certificate removed. ${versionHistory.length} version(s) in history.`
            : `Certificate removed from "${item.requirementName}"`
        });
      } else {
        // Update site_compliance_requirements to remove the evidence but keep history
        const { error } = await supabase
          .from('site_compliance_requirements')
          .update({
            evidence_url: null,
            evidence_file_id: null,
            evidence_file_name: null,
            evidence_version_history: versionHistory,
            status: 'not_started',
            last_completed_date: null
          })
          .eq('id', item.id);

        if (error) throw error;

        toast.success('Document removed', {
          id: loadingToast,
          description: versionHistory.length > 0
            ? `Document removed. ${versionHistory.length} version(s) in history.`
            : `Document unlinked from "${item.name}"`
        });
      }

      // Refresh data
      refetch();
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error('Failed to remove', { id: loadingToast });
    }
  }, [refetch]);

  // Handle opening edit expiry date dialog (for personnel certificates)
  const handleEditExpiry = useCallback((item) => {
    setEditExpiryItem(item);
    setSelectedExpiryDate(item.expiryDate ? new Date(item.expiryDate) : null);
    setEditExpiryOpen(true);
  }, []);

  // Handle saving expiry date
  const handleSaveExpiry = useCallback(async () => {
    if (!editExpiryItem?.id) return;

    const loadingToast = toast.loading('Updating expiry date...');
    try {
      const { error } = await supabase
        .from('user_qualifications')
        .update({
          expiry_date: selectedExpiryDate ? selectedExpiryDate.toISOString().split('T')[0] : null,
          updated_date: new Date().toISOString()
        })
        .eq('id', editExpiryItem.id);

      if (error) throw error;

      toast.success('Expiry date updated', { id: loadingToast });
      setEditExpiryOpen(false);
      setEditExpiryItem(null);
      setSelectedExpiryDate(null);
      refetch();
    } catch (error) {
      console.error('Error updating expiry date:', error);
      toast.error('Failed to update', { id: loadingToast });
    }
  }, [editExpiryItem, selectedExpiryDate, refetch]);

  // Handle upload completion - saves to user_qualifications
  const handleModalUpload = useCallback(async ({
    uploadedFilePath,
    expiryDate,
    certificateNumber,
    memberId,
    requirementId
  }) => {
    if (!uploadedFilePath || !memberId || !requirementId) {
      toast.error('Missing required upload data');
      return;
    }

    const loadingToast = toast.loading('Saving certificate...');

    try {
      // The uploaded file path is already a GCS path from UploadModal
      const publicUrl = gcsGetPublicUrl(uploadedFilePath);

      // Update user_qualifications record
      const { error: updateError } = await supabase
        .from('user_qualifications')
        .update({
          status: 'completed',
          renewal_status: 'completed',
          completed_date: new Date().toISOString().split('T')[0],
          expiry_date: expiryDate,
          evidence_url: publicUrl,
          certificate_number: certificateNumber || null,
          notes: `Certificate uploaded via Docs tool`,
          updated_date: new Date().toISOString(),
          verified_by: userProfile?.id,
          verified_date: new Date().toISOString()
        })
        .eq('user_id', memberId)
        .eq('requirement_id', requirementId);

      if (updateError) throw updateError;

      toast.success('Certificate saved successfully', { id: loadingToast });

      // Close modal and refresh data
      setUploadModalOpen(false);
      setSelectedUploadItem(null);
      refetch();

    } catch (error) {
      console.error('Error saving certificate:', error);
      toast.error('Failed to save certificate', { id: loadingToast });
    }
  }, [userProfile, refetch]);

  // Handle site document upload completion - saves to site_compliance_requirements
  const handleSiteModalUpload = useCallback(async ({
    uploadedFilePath,
    requirementId,
    file
  }) => {
    if (!uploadedFilePath || !requirementId) {
      toast.error('Missing required upload data');
      return;
    }

    const loadingToast = toast.loading('Saving document...');

    try {
      // The uploaded file path is already a GCS path from SiteDocumentUploadModal
      const siteDocPublicUrl = gcsGetPublicUrl(uploadedFilePath);

      // First, fetch current record to preserve version history
      const { data: currentRecord, error: fetchError } = await supabase
        .from('site_compliance_requirements')
        .select('evidence_url, evidence_file_name, evidence_version_history, last_completed_date')
        .eq('id', requirementId)
        .single();

      if (fetchError) throw fetchError;

      // Build version history - add current evidence to history if it exists
      let versionHistory = currentRecord?.evidence_version_history || [];
      if (currentRecord?.evidence_url) {
        versionHistory = [
          ...versionHistory,
          {
            url: currentRecord.evidence_url,
            file_name: currentRecord.evidence_file_name,
            uploaded_at: currentRecord.last_completed_date,
            replaced_at: new Date().toISOString()
          }
        ];
      }

      // Get the file name from the path or file object
      const fileName = file?.name || uploadedFilePath.split('/').pop();

      // Update site_compliance_requirements record
      const { error: updateError } = await supabase
        .from('site_compliance_requirements')
        .update({
          evidence_url: siteDocPublicUrl,
          evidence_file_name: fileName,
          evidence_version_history: versionHistory,
          status: 'compliant',
          last_completed_date: new Date().toISOString(),
          compliance_notes: `Document uploaded via Docs tool on ${new Date().toLocaleDateString()}`,
          updated_date: new Date().toISOString()
        })
        .eq('id', requirementId);

      if (updateError) throw updateError;

      toast.success('Document saved successfully', { id: loadingToast });

      // Close modal and refresh data
      setSiteUploadModalOpen(false);
      setSelectedSiteUploadItem(null);
      refetch();

    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document', { id: loadingToast });
    }
  }, [refetch]);

  // Drag event handlers for folder uploads
  const handleDragEnter = useCallback((e) => {
    if (e.dataTransfer?.types.includes('Files') && currentFolder) {
      e.preventDefault();
      if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
      setIsDraggingFiles(true);
    }
  }, [currentFolder]);

  const handleDragOver = useCallback((e) => {
    if (e.dataTransfer?.types.includes('Files') && currentFolder) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [currentFolder]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;
    if (x <= 0 || x >= window.innerWidth || y <= 0 || y >= window.innerHeight) {
      dragTimeoutRef.current = setTimeout(() => setIsDraggingFiles(false), 50);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
    // Direct upload via drag-drop is not supported - use the upload button on each row
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white rounded-lg border">
        <div className="flex-1 p-4 space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`h-full flex flex-col bg-white rounded-lg border overflow-hidden relative transition-all duration-200 ${
        isDraggingFiles ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-400' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column Headers (only when viewing files) */}
      {currentFolder && (
        <div className="flex items-center gap-3 px-4 py-2 border-b bg-gray-50 text-xs font-medium text-gray-500">
          <div className="w-4" /> {/* Icon spacer */}
          <div className="flex-1 min-w-0 w-[200px]">Name</div>
          <div className="w-[180px] flex-shrink-0">
            {currentFolder === 'personnel' ? 'Person' : 'Responsible'}
          </div>
          <div className="w-[120px] flex-shrink-0">Status</div>
          <div className="w-[120px] flex-shrink-0">
            {currentFolder === 'personnel' ? 'Expiry' : 'Due Date'}
          </div>
          <div className="w-[100px] flex-shrink-0" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {!currentFolder ? (
          // Root view - show folders
          <div>
            <FolderRow
              name="User Certificates"
              description="Personnel qualifications and training certificates"
              count={stats?.personnel?.total || 0}
              urgentCount={personnelUrgent}
              onClick={() => onFolderChange?.('personnel')}
            />
            <FolderRow
              name="Site Documents"
              description="Site compliance, inspections and certifications"
              count={stats?.site?.total || 0}
              urgentCount={siteUrgent}
              onClick={() => onFolderChange?.('site')}
            />
            <FolderRow
              name="Policies"
              description="Company policies requiring acknowledgment"
              count={stats?.policies?.total || 0}
              urgentCount={(stats?.policies?.overdue || 0) + (stats?.policies?.pending || 0)}
              onClick={() => onFolderChange?.('policies')}
            />
          </div>
        ) : currentFolder === 'personnel' ? (
          // Personnel certificates
          <>
            <ParentFolderRow onClick={() => onFolderChange?.(null)} />
            {personnelCerts.length > 0 ? (
              personnelCerts.map(item => (
                <FileRow
                  key={item.id}
                  item={item}
                  type="personnel"
                  onDownload={handleDownload}
                  onUpload={handleUpload}
                  onRemove={handleRemoveDocument}
                  onViewHistory={handleViewHistory}
                  onOpenFile={handleOpenFile}
                  onEditExpiry={handleEditExpiry}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FileText className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-sm">No certificates found</p>
              </div>
            )}
          </>
        ) : (
          // Site documents
          <>
            <ParentFolderRow onClick={() => onFolderChange?.(null)} />
            {siteDocs.length > 0 ? (
              siteDocs.map(item => (
                <FileRow
                  key={item.id}
                  item={item}
                  type="site"
                  onDownload={handleDownload}
                  onUpload={handleUpload}
                  onRemove={handleRemoveDocument}
                  onViewHistory={handleViewHistory}
                  onOpenFile={handleOpenFile}
                  onEditResponsible={handleEditResponsible}
                  onEditDueDate={handleEditDueDate}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FileText className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-sm">No documents found</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination Controls */}
      {currentFolder && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50">
          <div className="text-xs text-gray-500">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredTotal)} of {filteredTotal}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="h-3 w-3" />
              <ChevronLeft className="h-3 w-3 -ml-2" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <span className="text-xs text-gray-600 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-3 w-3" />
              <ChevronRight className="h-3 w-3 -ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Upload Modal for Personnel Certificates */}
      <Suspense fallback={null}>
        <UploadModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedUploadItem(null);
          }}
          selectedCell={selectedUploadItem}
          onUpload={handleModalUpload}
        />
      </Suspense>

      {/* Site Document Upload Modal */}
      <Suspense fallback={null}>
        <SiteDocumentUploadModal
          isOpen={siteUploadModalOpen}
          onClose={() => {
            setSiteUploadModalOpen(false);
            setSelectedSiteUploadItem(null);
          }}
          selectedRequirement={selectedSiteUploadItem}
          onUpload={handleSiteModalUpload}
        />
      </Suspense>

      {/* Version History Dialog */}
      <Dialog open={versionHistoryOpen} onOpenChange={setVersionHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-gray-500" />
              {versionHistoryType === 'personnel' ? 'Certificate History' : 'Document History'}
            </DialogTitle>
            <DialogDescription>
              {versionHistoryType === 'personnel'
                ? (versionHistoryItem?.requirementName && `Version history for "${versionHistoryItem.requirementName}"`)
                : (versionHistoryItem?.name && `Version history for "${versionHistoryItem.name}"`)
              }
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-3">
              {/* Current version */}
              {versionHistoryItem?.evidenceUrl && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-sm font-medium text-green-800">Current Version</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => window.open(versionHistoryItem.evidenceUrl, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                  {versionHistoryType === 'personnel' ? (
                    <>
                      {versionHistoryItem.expiryDate && (
                        <p className="text-xs text-green-700 mt-1">
                          Expires: {format(new Date(versionHistoryItem.expiryDate), 'dd MMM yyyy')}
                        </p>
                      )}
                      {versionHistoryItem.certificateNumber && (
                        <p className="text-xs text-green-600 mt-1">
                          Certificate #: {versionHistoryItem.certificateNumber}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      {versionHistoryItem.evidenceFileName && (
                        <p className="text-xs text-green-700 mt-1 truncate">
                          {versionHistoryItem.evidenceFileName}
                        </p>
                      )}
                      {versionHistoryItem.lastCompletedDate && (
                        <p className="text-xs text-green-600 mt-1">
                          Uploaded {format(new Date(versionHistoryItem.lastCompletedDate), 'dd MMM yyyy \'at\' HH:mm')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* No current version notice */}
              {!versionHistoryItem?.evidenceUrl && versionHistoryItem?.evidenceVersionHistory?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-500 rounded-full" />
                    <span className="text-sm font-medium text-amber-800">
                      No current {versionHistoryType === 'personnel' ? 'certificate' : 'document'}
                    </span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    The current {versionHistoryType === 'personnel' ? 'certificate' : 'document'} was removed. You can restore a previous version below.
                  </p>
                </div>
              )}

              {/* Previous versions */}
              {versionHistoryItem?.evidenceVersionHistory && versionHistoryItem.evidenceVersionHistory.length > 0 ? (
                <>
                  <div className="text-xs font-medium text-gray-500 pt-2">Previous Versions</div>
                  {[...versionHistoryItem.evidenceVersionHistory].reverse().map((version, reversedIndex) => {
                    // Calculate the original index (before reversing)
                    const originalIndex = versionHistoryItem.evidenceVersionHistory.length - 1 - reversedIndex;
                    const wasRemoved = !!version.removed_at;

                    return (
                      <div key={reversedIndex} className={`p-3 border rounded-lg ${wasRemoved ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${wasRemoved ? 'bg-red-400' : 'bg-gray-400'}`} />
                            <span className={`text-sm font-medium ${wasRemoved ? 'text-red-700' : 'text-gray-700'}`}>
                              Version {versionHistoryItem.evidenceVersionHistory.length - reversedIndex}
                              {wasRemoved && <span className="text-xs font-normal ml-1">(removed)</span>}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => handleViewVersionFile(version.url)}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleRevertToVersion(version, originalIndex)}
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              Restore
                            </Button>
                          </div>
                        </div>
                        {versionHistoryType === 'personnel' ? (
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {version.expiry_date && (
                              <span>Expires: {format(new Date(version.expiry_date), 'dd MMM yyyy')}</span>
                            )}
                            {version.certificate_number && (
                              <span>• #{version.certificate_number}</span>
                            )}
                          </div>
                        ) : (
                          version.file_name && (
                            <p className={`text-xs mt-1 truncate ${wasRemoved ? 'text-red-600' : 'text-gray-600'}`}>{version.file_name}</p>
                          )
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {version.uploaded_at && (
                            <span>Uploaded {format(new Date(version.uploaded_at), 'dd MMM yyyy')}</span>
                          )}
                          {version.replaced_at && (
                            <span>• Replaced {format(new Date(version.replaced_at), 'dd MMM yyyy')}</span>
                          )}
                          {version.removed_at && (
                            <span className="text-red-500">• Removed {format(new Date(version.removed_at), 'dd MMM yyyy')}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : !versionHistoryItem?.evidenceUrl ? (
                <div className="text-center py-6 text-gray-400">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No version history</p>
                </div>
              ) : null}
            </div>
          </ScrollArea>

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setVersionHistoryOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Responsible Person Dialog */}
      <Dialog open={editResponsibleOpen} onOpenChange={setEditResponsibleOpen}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-gray-500" />
              Assign Responsible Person
            </DialogTitle>
            <DialogDescription>
              {editResponsibleItem?.name && `Select who is responsible for "${editResponsibleItem.name}"`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 py-4">
            {loadingTeamMembers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
              </div>
            ) : teamMembers.length > 0 ? (
              <ScrollArea className="h-[300px] border rounded-lg">
                <div className="p-2 space-y-1">
                  {/* Option to clear/unassign */}
                  <button
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left hover:bg-gray-100 ${
                      !editResponsibleItem?.responsiblePersonId ? 'bg-violet-50 border border-violet-200' : ''
                    }`}
                    onClick={() => handleSaveResponsible(null)}
                  >
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Unassigned</p>
                      <p className="text-xs text-gray-400">No responsible person</p>
                    </div>
                  </button>

                  {teamMembers.map(member => {
                    const isSelected = editResponsibleItem?.responsiblePersonId === member.id;
                    const name = `${member.first_name || ''} ${member.last_name || ''}`.trim();
                    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

                    return (
                      <button
                        key={member.id}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left hover:bg-gray-100 ${
                          isSelected ? 'bg-violet-50 border border-violet-200' : ''
                        }`}
                        onClick={() => handleSaveResponsible(member.id)}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                          {member.email && (
                            <p className="text-xs text-gray-500 truncate">{member.email}</p>
                          )}
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="h-5 w-5 text-violet-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <User className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No team members found</p>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2 flex-shrink-0">
            <Button variant="outline" onClick={() => setEditResponsibleOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Due Date Dialog */}
      <Dialog open={editDueDateOpen} onOpenChange={setEditDueDateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-gray-500" />
              Edit Due Date
            </DialogTitle>
            <DialogDescription>
              {editDueDateItem?.name && `Set the due date for "${editDueDateItem.name}"`}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDueDate}
              onSelect={setSelectedDueDate}
              className="rounded-md border"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDueDate(null)}
              className="text-gray-500"
            >
              Clear date
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDueDateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveDueDate}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Expiry Date Dialog (for personnel certificates) */}
      <Dialog open={editExpiryOpen} onOpenChange={setEditExpiryOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-gray-500" />
              Edit Expiry Date
            </DialogTitle>
            <DialogDescription>
              {editExpiryItem?.requirementName && `Set the expiry date for "${editExpiryItem.requirementName}"`}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 flex justify-center">
            <Calendar
              mode="single"
              selected={selectedExpiryDate}
              onSelect={setSelectedExpiryDate}
              className="rounded-md border"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedExpiryDate(null)}
              className="text-gray-500"
            >
              Clear date
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditExpiryOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveExpiry}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drag Overlay */}
      {isDraggingFiles && currentFolder && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] flex items-center justify-center z-10 pointer-events-none">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center border-2 border-blue-400 border-dashed">
            <Upload className="h-12 w-12 text-blue-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-gray-900">Drop file here</p>
            <p className="text-sm text-gray-500 mt-1">Upload to {currentFolder === 'personnel' ? 'User Certificates' : 'Site Documents'}</p>
          </div>
        </div>
      )}

    </div>
  );
}
